import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { UserApiService } from 'src/app/services/user-api.service';
import { AddUserService } from '../../add-user/add-user.service';
import { AuthService } from 'src/app/core/services/auth.service';

// Domain types
interface UserId {
  value: string;
}
interface OrganizationId {
  value: string;
}
interface UserDomain {
  userId: UserId;
  firstname: string;
  lastname: string;
  externalId: string;
  npiNumber: string;
  email: string;
  username: string;
  active: boolean;
  authorizedOrgIds: OrganizationId[];
  excludeReferrals: boolean;
  selectedRoles: any[];
  orgId: OrganizationId | null;
  raw?: any;
}
interface UserFormValue {
  userId?: UserId;
  firstname: string;
  lastname: string;
  externalId: string;
  npiNumber: string;
  email: string;
  username: string;
  active: boolean;
  authorizedOrgIds: OrganizationId[] | string;
  excludeReferrals: boolean;
  selectedRoles: any[];
}

@Component({
  selector: 'app-edit-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
  ],
  templateUrl: './edit-user.component.html',
  styleUrl: './edit-user.component.scss',
})
export class EditUserComponent implements OnInit {
  formReady = false;
  showSuccess = false;
  isLoading = false;
  userForm!: FormGroup;
  user: UserDomain | undefined;
  orgId: string | null = null;
  userId: string | null = null;
  permissionSets: { permissionSetId: string; name: string }[] = [];
  orgName: string | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly userApi: UserApiService,
    private readonly addUserService: AddUserService,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    this.orgId = this.route.snapshot.paramMap.get('orgId');
    this.userId = this.route.snapshot.paramMap.get('userId');
    console.log('Edit user initialized with userId:', this.userId);
    this.setOrgName();
    if (!this.orgId) {
      this.user = undefined;
      this.initEmptyForm();
      return;
    }
    this.fetchPermissionSets();
  }

  private setOrgName() {
    const orgData = history.state?.orgName;
    if (orgData) {
      this.orgName = orgData;
    } else {
      this.subscribeOrgNameFromService();
    }
  }

  private subscribeOrgNameFromService() {
    if (this.addUserService?.['orgState']?.orgResponse$) {
      this.addUserService['orgState'].orgResponse$.subscribe((org) => {
        if (org?.name) {
          this.orgName = org.name;
        }
      });
    }
  }

  fetchPermissionSets() {
    this.loadPermissionSets();
    this.loadUserData();
  }

  private loadPermissionSets(): void {
    this.addUserService.getPermissionSets(this.orgId).subscribe((sets) => {
      this.permissionSets = this.addUserService['normalizePermissionSets'](sets);
      console.log('Permission sets loaded:', this.permissionSets);
    });
  }

  private loadUserData(): void {
    this.userApi.getUsersWithToken(this.orgId).subscribe((data) => {
      console.log('Raw user data from API:', data);
      
      const processedUsers = this.processUserData(data);
      this.selectTargetUser(processedUsers);
      this.initializeForm();
    });
  }

  private processUserData(data: any): UserDomain[] {
    const usersArr: any[] = Array.isArray(data?.matchingMsgs) ? data.matchingMsgs : [];
    console.log('Users array:', usersArr);
    
    const mappedUsers = usersArr.map((u: any) => this.mapUserFromMsg(u));
    console.log('Mapped users:', mappedUsers);
    
    const uniqueUsers = this.getUniqueUsersById(mappedUsers);
    console.log('Unique users:', uniqueUsers);
    
    return uniqueUsers;
  }

  private selectTargetUser(uniqueUsers: UserDomain[]): void {
    this.user = uniqueUsers.find(
      (u: UserDomain) => (u.userId.value || '').trim() === (this.userId || '').trim()
    ) || uniqueUsers[0];

    if (!this.user && uniqueUsers.length > 0) {
      console.warn(
        'User not found for userId',
        this.userId,
        'in',
        uniqueUsers.map((u: UserDomain) => u.userId.value)
      );
      this.user = uniqueUsers[0];
    }
    
    console.log('Final selected user before form init:', this.user);
    console.log('User ID we are looking for:', this.userId);
  }

  private initializeForm(): void {
    if (this.user) {
      this.initForm();
    } else {
      console.error('No user data found, initializing empty form');
      this.initEmptyForm();
    }
  }

  private mapUserFromMsg(u: any): UserDomain | any {
    if (!u?.msgValue) return u;
    try {
      const parsed = JSON.parse(u.msgValue);
      return this.extractUserFields(parsed, u);
    } catch {
      return u;
    }
  }

  private getIdFromParsed(parsed: any, criteria: { type: string; system: string }): UserId {
    if (!Array.isArray(parsed.identifiers)) return { value: '' };
    const found = parsed.identifiers.find(
      (id: any) => id.type === criteria.type && id.system === criteria.system
    );
    return { value: found?.value || '' };
  }

  private getFieldFromParsed(config: { obj: any; field: string; fallback?: any }): any {
    return config.obj?.[config.field] ?? (config.fallback || '');
  }

  private getNameFields(
    parsed: any
  ): Pick<UserDomain, 'firstname' | 'lastname'> {
    return {
      firstname: this.getFieldFromParsed({
        obj: parsed,
        field: 'firstName',
        fallback: this.getFieldFromParsed({ obj: parsed.name, field: 'given', fallback: '' })
      }),
      lastname: this.getFieldFromParsed({
        obj: parsed,
        field: 'lastName',
        fallback: this.getFieldFromParsed({ obj: parsed.name, field: 'family', fallback: '' })
      }),
    };
  }

  private getOrgId(parsed: any): OrganizationId | null {
    const val = parsed.partOf?.identifier?.value || this.orgId;
    return val ? { value: val } : null;
  }

  private getAuthorizedOrgIdsFromParsed(
    orgId: OrganizationId | null
  ): OrganizationId[] {
    return orgId ? [orgId] : [];
  }

  private getEmailFromParsed(parsed: any): string {
    if (!Array.isArray(parsed.telecoms)) return '';
    const emailObj = parsed.telecoms.find((t: any) => t.system === 'Email');
    return emailObj?.value || '';
  }

  private getSelectedRolesFromParsed(parsed: any): any[] {
    return Array.isArray(parsed.permissionSetIdentifiers)
      ? parsed.permissionSetIdentifiers
      : [];
  }

  private parseExcludeReferrals(val: any): boolean {
    if (typeof val === 'string') return val === 'true';
    if (typeof val === 'boolean') return val;
    return !!val;
  }

  private extractUserFields(parsed: any, raw: any): UserDomain {
    const nameFields = this.getNameFields(parsed);
    const orgId = this.getOrgId(parsed);

    return {
      userId: this.getIdFromParsed(parsed, { type: 'UUID', system: 'https://philips.com/careorchestrator' }),
      firstname: nameFields.firstname,
      lastname: nameFields.lastname,
      externalId:
        this.getIdFromParsed(parsed, { type: 'String', system: 'https://philips.com/externalId' }).value ||
        this.getIdFromParsed(parsed, { type: 'UUID', system: 'https://philips.com/careorchestrator' }).value,
      npiNumber: this.getIdFromParsed(parsed, { type: 'String', system: 'https://philips.com/npi' }).value,
      email: this.getEmailFromParsed(parsed),
      username: this.getFieldFromParsed({ obj: parsed, field: 'userName', fallback: '' }),
      active: typeof parsed.active === 'boolean' ? parsed.active : false,
      authorizedOrgIds: this.getAuthorizedOrgIdsFromParsed(orgId),
      excludeReferrals: this.parseExcludeReferrals(parsed.excludeReferrals),
      selectedRoles: this.getSelectedRolesFromParsed(parsed),
      orgId,
      raw,
    };
  }

  private getUniqueUsersById(users: UserDomain[]): UserDomain[] {
    const uniqueUsersMap = new Map();
    for (let i = users.length - 1; i >= 0; i--) {
      const user = users[i];
      if (user?.userId.value && !uniqueUsersMap.has(user.userId.value)) {
        uniqueUsersMap.set(user.userId.value, user);
      }
    }
    return Array.from(uniqueUsersMap.values()).reverse();
  }

  private isActiveChanged(formValue: UserFormValue): boolean {
    return this.user?.active !== !!formValue.active;
  }

  private getUserIdFromForm(formValue: UserFormValue): UserId {
    return (
      formValue.userId || { value: this.userId || '' } ||
      this.user?.userId || { value: '' }
    );
  }

  private onSubmitActions(
    formValue: UserFormValue,
    userIdFromUI: UserId,
    authToken: string
  ) {
    const originalActive = this.user?.active;
    const newActive = !!formValue.active;
    const otherFieldsChanged = this.haveOtherFieldsChanged(
      formValue,
      this.user as UserDomain
    );

    if (originalActive !== newActive && !otherFieldsChanged) {
      this.handleActiveChange({ userId: userIdFromUI, newActive, authToken });
      return;
    }

    if (otherFieldsChanged) {
      this.handleUserUpdate({ userId: userIdFromUI, formValue, authToken });
      return;
    }

    // No changes to submit, just navigate back
    this.isLoading = false;
    this.navigateToShowUser();
  }

  onSubmit() {
    if (this.userForm.invalid || !this.user) return;

    this.isLoading = true;
    this.showSuccess = false;
    
    const formValue: UserFormValue = this.userForm.value;
    const userIdFromUI = this.getUserIdFromForm(formValue);
    const authToken = this.authService.token;

    this.onSubmitActions(formValue, userIdFromUI, authToken);
  }

  private haveOtherFieldsChanged(
    formValue: UserFormValue,
    user: UserDomain
  ): boolean {
    // Helper for array comparison
    const arraysEqual = (a: any[], b: any[]) =>
      Array.isArray(a) && Array.isArray(b)
        ? a.join(',') === b.join(',')
        : false;

    const fieldsToCheck: (keyof UserFormValue)[] = [
      'firstname',
      'lastname',
      'externalId',
      'npiNumber',
      'email',
      'username',
      'authorizedOrgIds',
      'excludeReferrals',
      'selectedRoles',
    ];
    return fieldsToCheck.some((field) => {
      if (Array.isArray(formValue[field]) && Array.isArray(user[field])) {
        return !arraysEqual(formValue[field] as any[], user[field] as any[]);
      }
      return (formValue[field] ?? '') !== (user[field] ?? '');
    });
  }

  private handleActiveChange(params: {
    userId: UserId;
    newActive: boolean;
    authToken: string;
  }) {
    const action$ = params.newActive
      ? this.userApi.activateUser(params.userId.value, params.authToken)
      : this.userApi.deactivateUser(params.userId.value, params.authToken);
    action$.subscribe({
      next: () => {
        this.isLoading = false;
        this.showSuccess = true;
        setTimeout(() => this.navigateToShowUser(), 2000);
      },
      error: (err) => {
        this.isLoading = false;
        alert('Failed to change active status: ' + (err?.message || err));
        this.navigateToShowUser();
      },
    });
  }

  private handleUserUpdate(params: {
    userId: UserId;
    formValue: UserFormValue;
    authToken: string;
  }) {
    const updatedUser = this.buildUpdatedUser(params.userId, params.formValue);
    this.userApi.updateUser(params.userId.value, updatedUser, params.authToken).subscribe({
      next: () => {
        this.isLoading = false;
        this.showSuccess = true;
        setTimeout(() => this.navigateToShowUser(), 2000);
      },
      error: (err) => {
        this.isLoading = false;
        alert('Failed to update user: ' + (err?.message || err));
      },
    });
  }

  private buildUpdatedUser(userId: UserId, formValue: UserFormValue): any {
    // Helper for authorizedOrgIds
    const parseAuthorizedOrgIds = (value: any) =>
      typeof value === 'string'
        ? value
            .split(',')
            .map((id: string) => id.trim())
            .filter((id: string) => !!id)
        : Array.isArray(value)
        ? value.map((org: OrganizationId) => org.value)
        : [];

    // Helper for selectedRoles
    const parseSelectedRoles = (value: any) =>
      Array.isArray(value) ? value : [];

    // Always send firstName and lastName (API expects these exact keys)
    return {
      userId: userId.value,
      firstName: formValue.firstname,
      lastName: formValue.lastname, 
      externalId: formValue.externalId,
      npiNumber: formValue.npiNumber,
      email: formValue.email,
      username: formValue.username,
      active: formValue.active,
      authorizedOrgIds: parseAuthorizedOrgIds(formValue.authorizedOrgIds),
      excludeReferrals: !!formValue.excludeReferrals,
      selectedRoles: parseSelectedRoles(formValue.selectedRoles),
      orgId: this.orgId,
    };
  }

  private navigateToShowUser() {
    if (this.orgId) {
      this.router.navigate(['/organization/show-user', this.orgId], {
        queryParams: { updated: '1' },
      });
    }
  }

  private getExternalId(): string {
    return this.user?.externalId ?? '';
  }

  private getAuthorizedOrgIds(): string {
    // Bind the 0th value from authorizedOrgIds array if available
    if (
      Array.isArray(this.user?.authorizedOrgIds) &&
      this.user.authorizedOrgIds.length > 0
    ) {
      return this.user.authorizedOrgIds[0].value;
    }
    return '';
  }

  private getSelectedRoles(): any[] {
    return Array.isArray(this.user?.selectedRoles)
      ? this.user.selectedRoles
      : [];
  }

  private getBooleanField(config: { field: string; defaultValue: boolean }): boolean {
    return typeof this.user?.[config.field] === 'boolean'
      ? this.user[config.field]
      : config.defaultValue;
  }

  private getSimpleField(config: { field: string; defaultValue?: any }): any {
    return this.user?.[config.field] ?? (config.defaultValue || '');
  }

  private getFieldValue(config: { field: string; fallback?: any }): any {
    const fieldGetters: { [key: string]: () => any } = {
      externalId: () => this.getExternalId(),
      authorizedOrgIds: () => this.getAuthorizedOrgIds(),
      selectedRoles: () => this.getSelectedRoles(),
      firstname: () => this.getSimpleField({ field: 'firstname' }),
      lastname: () => this.getSimpleField({ field: 'lastname' }),
      npiNumber: () => this.getSimpleField({ field: 'npiNumber' }),
      email: () => this.getSimpleField({ field: 'email' }),
      username: () => this.getSimpleField({ field: 'username' }),
      active: () => this.getBooleanField({ field: 'active', defaultValue: true }),
      excludeReferrals: () => this.getBooleanField({ field: 'excludeReferrals', defaultValue: false }),
    };
    return fieldGetters[config.field]
      ? fieldGetters[config.field]()
      : this.getSimpleField({ field: config.field, defaultValue: config.fallback });
  }

  private initForm() {
    console.log('Initializing form with user data:', this.user);
    
    if (!this.user) {
      console.warn('No user data available for form initialization');
      this.initEmptyForm();
      return;
    }

    const formValues = this.extractFormValues();
    this.logFormValues(formValues);
    this.createFormWithValues(formValues);
    this.setupFormChangeSubscription();
    this.formReady = true;
  }

  private extractFormValues(): any {
    const authorizedOrgIds = this.extractAuthorizedOrgIds();
    const selectedRoles = this.extractSelectedRoles();

    return {
      firstname: this.user!.firstname || '',
      lastname: this.user!.lastname || '',
      externalId: this.user!.externalId || '',
      npiNumber: this.user!.npiNumber || '',
      email: this.user!.email || '',
      username: this.user!.username || '',
      active: this.user!.active ?? true,
      excludeReferrals: this.user!.excludeReferrals ?? false,
      authorizedOrgIds,
      selectedRoles
    };
  }

  private extractAuthorizedOrgIds(): string {
    if (Array.isArray(this.user!.authorizedOrgIds) && this.user!.authorizedOrgIds.length > 0) {
      return this.user!.authorizedOrgIds[0].value || '';
    }
    return '';
  }

  private extractSelectedRoles(): any[] {
    return Array.isArray(this.user!.selectedRoles) ? this.user!.selectedRoles : [];
  }

  private logFormValues(values: any): void {
    console.log('Extracted values:');
    Object.entries(values).forEach(([key, value]) => {
      console.log(`${key}:`, value);
    });
  }

  private createFormWithValues(values: any): void {
    this.userForm = this.fb.group({
      firstname: [values.firstname, Validators.required],
      lastname: [values.lastname, Validators.required],
      externalId: [values.externalId],
      npiNumber: [values.npiNumber],
      email: [values.email, [Validators.required, Validators.email]],
      username: [values.username, Validators.required],
      active: [values.active],
      authorizedOrgIds: [values.authorizedOrgIds],
      excludeReferrals: [values.excludeReferrals],
      selectedRoles: [values.selectedRoles]
    });
    
    console.log('Form values after initialization:', this.userForm.value);
  }

  private setupFormChangeSubscription(): void {
    this.userForm.valueChanges.subscribe(() => {
      // Optionally, you can enable/disable the save button based on dirty state
    });
  }

  private initEmptyForm() {
    const formControlsConfig: { [key: string]: any } = {};
    const emptyFieldsConfig = [
      { key: 'firstname', validators: Validators.required, defaultValue: '' },
      { key: 'lastname', validators: Validators.required, defaultValue: '' },
      { key: 'externalId', defaultValue: '' },
      { key: 'npiNumber', defaultValue: '' },
      { key: 'email', validators: [Validators.required, Validators.email], defaultValue: '' },
      { key: 'username', validators: Validators.required, defaultValue: '' },
      { key: 'active', defaultValue: true },
      { key: 'authorizedOrgIds', defaultValue: '' },
      { key: 'excludeReferrals', defaultValue: false },
      { key: 'selectedRoles', defaultValue: [] },
    ];

    for (const { key, validators, defaultValue } of emptyFieldsConfig) {
      formControlsConfig[key] = validators
        ? [defaultValue, validators]
        : [defaultValue];
    }

    this.userForm = this.fb.group(formControlsConfig);
    this.formReady = true;
  }

  onRefreshUsername(): void {
    const names = this.extractNamesFromForm();

    if (!this.areNamesValid(names)) {
      return;
    }
    
    console.log(`Generating username for: ${names.firstName} ${names.lastName}`);
    
    this.addUserService.generateUsername(names.firstName, names.lastName).subscribe({
      next: (response) => {
        console.log('Username generation response:', response);
        if (response?.username) {
          console.log(`Setting username to: ${response.username}`);
          this.userForm.patchValue({ username: response.username });
          this.markFormAsChanged();
        } else {
          console.warn('No username in response, using fallback');
          this.setFallbackUsername(names);
        }
      },
      error: (error) => {
        console.warn('Failed to generate username:', error);
        this.setFallbackUsername(names);
      }
    });
  }

  private extractNamesFromForm(): { firstName: string; lastName: string } {
    return {
      firstName: this.userForm.get('firstname')?.value?.trim() || '',
      lastName: this.userForm.get('lastname')?.value?.trim() || ''
    };
  }

  private areNamesValid(names: { firstName: string; lastName: string }): boolean {
    return names.firstName && names.lastName && names.firstName.length > 0 && names.lastName.length > 0;
  }

  onFirstNameBlur(): void {
    console.log('First name blur triggered');
    this.userForm.get('firstname')?.markAsTouched();
    this.tryAutoGenerateUsername();
  }

  onLastNameBlur(): void {
    console.log('Last name blur triggered');
    this.userForm.get('lastname')?.markAsTouched();
    this.tryAutoGenerateUsername();
  }

  private tryAutoGenerateUsername(): void {
    const names = this.extractNamesFromForm();
    console.log('tryAutoGenerateUsername called with names:', names);
    
    // Only auto-generate if both names are valid
    if (!this.areNamesValid(names)) {
      console.log('Names not valid, skipping auto-generation');
      return;
    }

    // For now, always generate if both names are present (we can add change detection later if needed)
    console.log('Names are valid, proceeding with auto-generation');
    this.generateUsernameInternal(names);
  }

  private generateUsernameInternal(names: { firstName: string; lastName: string }): void {
    console.log(`Auto-generating username for: ${names.firstName} ${names.lastName}`);
    
    this.addUserService.generateUsername(names.firstName, names.lastName).subscribe({
      next: (response) => {
        console.log('Auto-generated username response:', response);
        if (response?.username) {
          console.log(`Auto-setting username to: ${response.username}`);
          this.userForm.patchValue({ username: response.username });
          this.markFormAsChanged();
        } else {
          console.warn('No username in auto-generation response, using fallback');
          this.setFallbackUsername(names);
        }
      },
      error: (error) => {
        console.warn('Failed to auto-generate username:', error);
        this.setFallbackUsername(names);
      }
    });
  }

  private setFallbackUsername(names: { firstName: string; lastName: string }): void {
    const fallbackUsername = `${names.firstName.toLowerCase()}.${names.lastName.toLowerCase()}${this.addUserService.randomString(4)}`;
    console.log(`Using fallback username: ${fallbackUsername}`);
    this.userForm.patchValue({ username: fallbackUsername });
    this.markFormAsChanged();
  }

  private markFormAsChanged(): void {
    // Mark the username field as dirty and touched to trigger change detection
    const usernameControl = this.userForm.get('username');
    if (usernameControl) {
      usernameControl.markAsDirty();
      usernameControl.markAsTouched();
    }
    
    // Mark the entire form as dirty to enable save button
    this.userForm.markAsDirty();
  }

  goBack() {
    if (this.orgId) {
      this.router.navigate(['/organization/show-user', this.orgId]);
    }
  }
}
